import { useTranslation } from '../i18n';
import { LINKS } from '../brand';
import { Eyebrow, PlatformFootnote } from '../primitives';

/**
 * Multi-column editorial footer, used by the landing page. Four columns at desktop, collapses
 * to a 2-column grid below sm. PlatformFootnote in `embedded` mode contributes the
 * copyright + FFXIV-disclaimer strip at the bottom so that row stays visually identical to the
 * catalog/admin footers.
 *
 *   ┌──────────────────────────┬─────────────┬─────────────┬─────────────┐
 *   │ COLOPHON                 │ PLATFORM    │ PROJECT     │ COMMUNITY   │
 *   │ Made by the community... │ • Emporium  │ • GitHub    │ • Discord   │
 *   │                          │ • Studio    │ • Releases  │ • Sources   │
 *   │                          │ • Manual    │ • Contribute│ • Press kit │
 *   │                          │ • Curators  │ • Trouble.. │ • Licenses  │
 *   └──────────────────────────┴─────────────┴─────────────┴─────────────┘
 *   © 2023–2026 Gposingway · A community patch · CC BY 4.0    FFXIV © Square Enix...
 */
export function Colophon({
    emporiumHref,
    studioHref,
    packHref,
}: {
    emporiumHref: string;
    studioHref: string;
    packHref: string;
}) {
    const t = useTranslation();

    return (
        <footer class="border-t border-rule font-mono text-[11.5px] tracking-[0.04em] text-fg-muted">
            <div class="mx-auto max-w-[1680px] px-[clamp(20px,3.5vw,56px)] py-16">
                <div class="mb-12 grid grid-cols-2 gap-8 sm:grid-cols-[2fr_1fr_1fr_1fr]">
                    <div>
                        <Eyebrow as="h4" tone="faint" class="mb-4">
                            {t('landing.colophonHeading')}
                        </Eyebrow>
                        <p class="max-w-[36ch] font-display text-[22px] italic leading-[1.35] text-fg-primary">
                            {t('landing.colophonCredo')}
                        </p>
                    </div>

                    <div>
                        <Eyebrow as="h4" tone="faint" class="mb-4">
                            {t('landing.colophonPlatform')}
                        </Eyebrow>
                        <LinkList
                            items={[
                                { href: emporiumHref, label: t('landing.colophonPlatformEmporium') },
                                { href: studioHref, label: t('landing.colophonPlatformStudio') },
                                { href: LINKS.github.installGuide, label: t('landing.colophonPlatformManual') },
                                { href: emporiumHref, label: t('landing.colophonPlatformCurators') },
                            ]}
                        />
                    </div>

                    <div>
                        <Eyebrow as="h4" tone="faint" class="mb-4">
                            {t('landing.colophonProject')}
                        </Eyebrow>
                        <LinkList
                            items={[
                                { href: LINKS.github.pack, label: t('landing.colophonProjectGithub') },
                                { href: LINKS.github.releasesLatest, label: t('landing.colophonProjectReleases') },
                                { href: `${LINKS.github.pack}/blob/main/CONTRIBUTING.md`, label: t('landing.colophonProjectContribute') },
                                { href: packHref, label: t('landing.colophonProjectTroubleshoot') },
                            ]}
                        />
                    </div>

                    <div>
                        <Eyebrow as="h4" tone="faint" class="mb-4">
                            {t('landing.colophonCommunity')}
                        </Eyebrow>
                        <LinkList
                            items={[
                                { href: LINKS.discord.invite, label: t('landing.colophonCommunityDiscord') },
                                { href: emporiumHref, label: t('landing.colophonCommunitySources') },
                                { href: '#', label: t('landing.colophonCommunityPressKit') },
                                { href: '#', label: t('landing.colophonCommunityLicenses') },
                            ]}
                        />
                    </div>
                </div>

                <PlatformFootnote
                    embedded
                    left={t('landing.colophonCopyright')}
                    right={t('landing.colophonFfxiv')}
                />
            </div>
        </footer>
    );
}

function LinkList({ items }: { items: { href: string; label: string }[] }) {
    return (
        <ul class="flex flex-col gap-2.5">
            {items.map((item) => (
                <li key={item.label}>
                    <a href={item.href} class="transition-colors hover:text-fg-primary">
                        {item.label}
                    </a>
                </li>
            ))}
        </ul>
    );
}
